import { Service } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  Carriers,
  UpsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { getAllCarrierServiceCombinations } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RedoTagsDropdownInput,
  ValidBadgeRedoListItems,
} from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { getCarrierServiceLabel } from "@redotech/redo-web/carrier-service-labels";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { memo, useContext, useMemo, useState } from "react";
import { AuthContext } from "../../../app/auth";
import { OutboundLabelForm } from "../outbound-labels";
import {
  DhlDirectIntegrationSettings,
  dhlDirectIntegrationSettingsForm,
} from "./dhl-direct-integration-settings";
import { OverboxFulfillmentCenterSelect } from "./overbox-fulfillment-center-select";
import {
  UniUniDirectIntegrationSettings,
  uniUniDirectIntegrationSettingsForm,
} from "./uni-uni-direct-integration-settings";

const directToCarrierIntegrationsCarrierAccountForm = groupInput({
  ups: input<boolean>(),
  fedex: input<boolean>(),
  usps: input<boolean>(),
  dhl: input<boolean>(),
  dhlInternational: input<boolean>(),
  canadaPost: input<boolean>(),
});

const internationalBridgeForm = groupInput({ usps: input<boolean>() });

export interface BlockedServiceLevel {
  carrier: Carriers;
  service: Service;
}

const blockedServiceLevelsForm = input<BlockedServiceLevel[]>();

export const carrierSettingsForm = groupInput({
  directToCarrierIntegrations: directToCarrierIntegrationsCarrierAccountForm,
  internationalBridge: internationalBridgeForm,
  blockedServiceLevels: blockedServiceLevelsForm,
  dhlDirectIntegrationSettings: dhlDirectIntegrationSettingsForm,
  uniUni: uniUniDirectIntegrationSettingsForm,
  useAustraliaPostCarrierAccount: input<boolean>(),
  useEpostGlobalCarrierAccount: input<boolean>(),
  useApcCarrierAccount: input<boolean>(),
  useDhlExpressCarrierAccount: input<boolean>(),
  apcFulfillmentCenter: input<string | null>(),
  epgFulfillmentCenter: input<string | null>(),
});

export type CarrierSettingsForm = InputSchema.Input<typeof carrierSettingsForm>;

export type CarrierSettingsValue = InputSchema.Value<
  typeof carrierSettingsForm
>;

export const carrierSettingsDefault: CarrierSettingsValue = {
  directToCarrierIntegrations: {
    ups: true,
    fedex: false,
    usps: true,
    dhl: false,
    dhlInternational: false,
    canadaPost: false,
  },
  internationalBridge: { usps: true },
  blockedServiceLevels: [],
  dhlDirectIntegrationSettings: {
    pickupId: "",
    distributionCenter: "",
    useDefaultForAllLocations: false,
  },
  uniUni: { enabled: false, pickupWarehouseId: null, customerId: "" },
  useAustraliaPostCarrierAccount: false,
  useEpostGlobalCarrierAccount: false,
  useApcCarrierAccount: false,
  useDhlExpressCarrierAccount: false,
  apcFulfillmentCenter: null,
  epgFulfillmentCenter: null,
};

export const CarrierSettingsCard = memo(function CarrierSettingsCard({
  input,
  omsEngineers,
}: {
  input: OutboundLabelForm;
  omsEngineers: string[];
}) {
  const auth = useContext(AuthContext);
  const { carrierSettings } = input.inputs;
  const [searchString, setSearchString] = useState("");

  const blockableServiceLevels = useMemo(
    () => getAllCarrierServiceCombinations(),
    [],
  );

  const getLabel = (carrier: Carriers, service: Service) => {
    if (service === UpsServices.SurePostUnder1Lb) {
      return "UPS Ground Saver® (under 1lb)";
    }
    if (service === UpsServices.SurePostOver1Lb) {
      return "UPS Ground Saver® (over 1lb)";
    }
    return getCarrierServiceLabel(carrier, service);
  };

  const blockableServiceLevelsItems: ValidBadgeRedoListItems<BlockedServiceLevel>[] =
    useMemo(() => {
      const allItems = Object.values(blockableServiceLevels).map((service) => ({
        type: "badge" as const,
        id: `${service.carrier} - ${service.service}`,
        value: service,
        badge: { text: getLabel(service.carrier, service.service) },
      }));

      if (!searchString) {
        return allItems;
      }

      const searchLower = searchString.toLowerCase();
      return allItems.filter((item) =>
        item.badge.text.toLowerCase().includes(searchLower),
      );
    }, [blockableServiceLevels, searchString]);

  const selectedServiceLevels: BadgeRedoListItem<BlockedServiceLevel>[] =
    useMemo(() => {
      return carrierSettings.inputs.blockedServiceLevels.value.map(
        (blocked) => ({
          type: "badge" as const,
          id: `${blocked.carrier} - ${blocked.service}`,
          value: blocked,
          badge: { text: getLabel(blocked.carrier, blocked.service) },
        }),
      );
    }, [carrierSettings.inputs.blockedServiceLevels.value]);

  const upsEnabled =
    carrierSettings.inputs.directToCarrierIntegrations.inputs.ups.value;
  const fedexEnabled =
    carrierSettings.inputs.directToCarrierIntegrations.inputs.fedex.value;
  const uspsEnabled =
    carrierSettings.inputs.directToCarrierIntegrations.inputs.usps.value;
  const dhlEnabled =
    carrierSettings.inputs.directToCarrierIntegrations.inputs.dhl.value;
  const dhlInternationalEnabled =
    carrierSettings.inputs.directToCarrierIntegrations.inputs.dhlInternational
      .value;
  const internationalBridgeEnabled =
    carrierSettings.inputs.internationalBridge.inputs.usps.value;
  const canadaPostEnabled =
    carrierSettings.inputs.directToCarrierIntegrations.inputs.canadaPost.value;
  const uniUniEnabled = carrierSettings.inputs.uniUni.inputs.enabled.value;

  return (
    <Card title="Carrier Settings">
      <Flex dir="column" gap="md">
        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="semibold">
            Direct Carrier Integrations
          </Text>
          <RedoToggle
            label={`UPS Direct Integration ${upsEnabled ? "Enabled" : "Disabled"}`}
            setValue={
              carrierSettings.inputs.directToCarrierIntegrations.inputs.ups
                .setValue
            }
            value={upsEnabled}
          />
          <RedoToggle
            disabled={!omsEngineers.includes(auth?.email ?? "")}
            label={`FedEx Direct Integration ${fedexEnabled ? "Enabled" : "Disabled"}`}
            setValue={
              carrierSettings.inputs.directToCarrierIntegrations.inputs.fedex
                .setValue
            }
            value={fedexEnabled}
          />
          <RedoToggle
            disabled={!omsEngineers.includes(auth?.email ?? "")}
            label={`USPS Direct Integration ${uspsEnabled ? "Enabled" : "Disabled"}`}
            setValue={
              carrierSettings.inputs.directToCarrierIntegrations.inputs.usps
                .setValue
            }
            value={uspsEnabled}
          />
          <RedoToggle
            label={`Canada Post Direct Integration ${canadaPostEnabled ? "Enabled" : "Disabled"}`}
            setValue={
              carrierSettings.inputs.directToCarrierIntegrations.inputs
                .canadaPost.setValue
            }
            value={canadaPostEnabled}
          />
          <RedoToggle
            label={`DHL Direct Integration ${dhlEnabled ? "Enabled" : "Disabled"}`}
            setValue={
              carrierSettings.inputs.directToCarrierIntegrations.inputs.dhl
                .setValue
            }
            value={dhlEnabled}
          />
          <RedoToggle
            label={`DHL International Direct Integration ${dhlInternationalEnabled ? "Enabled" : "Disabled"}`}
            setValue={
              carrierSettings.inputs.directToCarrierIntegrations.inputs
                .dhlInternational.setValue
            }
            value={dhlInternationalEnabled}
          />
          {dhlEnabled && (
            <DhlDirectIntegrationSettings
              input={carrierSettings.inputs.dhlDirectIntegrationSettings}
            />
          )}
        </Flex>

        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="semibold">
            UniUni Direct Integration Settings
          </Text>
          <RedoToggle
            label={`ePost Global Direct Integration ${
              carrierSettings.inputs.useEpostGlobalCarrierAccount.value
                ? "Enabled"
                : "Disabled"
            }`}
            setValue={
              carrierSettings.inputs.useEpostGlobalCarrierAccount.setValue
            }
            value={carrierSettings.inputs.useEpostGlobalCarrierAccount.value}
          />
          {carrierSettings.inputs.useEpostGlobalCarrierAccount.value && (
            <OverboxFulfillmentCenterSelect
              input={carrierSettings.inputs.epgFulfillmentCenter}
              service="epg"
            />
          )}
          <RedoToggle
            label={`APC Direct Integration ${
              carrierSettings.inputs.useApcCarrierAccount.value
                ? "Enabled"
                : "Disabled"
            }`}
            setValue={carrierSettings.inputs.useApcCarrierAccount.setValue}
            value={carrierSettings.inputs.useApcCarrierAccount.value}
          />
          {carrierSettings.inputs.useApcCarrierAccount.value && (
            <OverboxFulfillmentCenterSelect
              input={carrierSettings.inputs.apcFulfillmentCenter}
              service="apc"
            />
          )}
          <RedoToggle
            label={`DHL Express Carrier Account ${
              carrierSettings.inputs.useDhlExpressCarrierAccount.value
                ? "Enabled"
                : "Disabled"
            }`}
            setValue={
              carrierSettings.inputs.useDhlExpressCarrierAccount.setValue
            }
            value={carrierSettings.inputs.useDhlExpressCarrierAccount.value}
          />
          <RedoToggle
            label={`UniUni Direct Integration ${uniUniEnabled ? "Enabled" : "Disabled"}`}
            setValue={carrierSettings.inputs.uniUni.inputs.enabled.setValue}
            value={uniUniEnabled}
          />
          {uniUniEnabled && (
            <UniUniDirectIntegrationSettings
              input={carrierSettings.inputs.uniUni}
            />
          )}
        </Flex>

        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="semibold">
            Additional Carrier Settings
          </Text>
          <RedoToggle
            label={`International Bridge Integration ${internationalBridgeEnabled ? "Enabled" : "Disabled"}`}
            setValue={
              carrierSettings.inputs.internationalBridge.inputs.usps.setValue
            }
            value={internationalBridgeEnabled}
          />
          <RedoToggle
            label="Enable Australia Post Carrier Account"
            setValue={
              carrierSettings.inputs.useAustraliaPostCarrierAccount.setValue
            }
            value={carrierSettings.inputs.useAustraliaPostCarrierAccount.value}
          />
        </Flex>

        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="semibold">
            Service Level Blocking
          </Text>
          <Text fontSize="xs" mt="xs">
            Select specific service levels to block:
          </Text>
          <RedoTagsDropdownInput
            label=""
            options={blockableServiceLevelsItems}
            placeholder="Select service levels to block..."
            searchPlaceholder="Search carriers and services..."
            searchString={searchString}
            selectedOptions={selectedServiceLevels}
            setSearchString={setSearchString}
            setSelectedOptions={(selected) => {
              const blockedLevels = selected.map((item) => item.value);
              carrierSettings.inputs.blockedServiceLevels.setValue(
                blockedLevels,
              );
            }}
            size="md"
          />
        </Flex>
      </Flex>
    </Card>
  );
});
